import { api } from './api'

export type RuleType = 'screening' | 'diagnostic' | 'treatment' | 'monitoring' | 'alert' | 'workflow'
export type RuleSeverity = 'low' | 'medium' | 'high' | 'critical'
export type RuleStatus = 'draft' | 'pending_approval' | 'active' | 'paused' | 'archived'
export type RuleTrigger = 'prescription_created' | 'lab_result_completed' | 'appointment_scheduled' | 'patient_registered' | 'vital_signs_recorded' | 'manual' | 'scheduled'
export type ConditionOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'not_contains' | 'in' | 'not_in' | 'between' | 'exists' | 'not_exists' | 'starts_with' | 'ends_with'
export type ActionType = 'CREATE_ALERT' | 'SEND_NOTIFICATION' | 'SCHEDULE_FOLLOWUP' | 'ADD_TO_LIST' | 'UPDATE_FIELD' | 'CREATE_TASK' | 'ESCALATE'
export type AlertStatus = 'new' | 'acknowledged' | 'dismissed' | 'resolved'

export interface ClinicalRuleConditionBase {
  name?: string | null
  description?: string | null
  field_path: string
  operator: ConditionOperator
  value?: string | null
  value_type: 'string' | 'number' | 'boolean' | 'date' | 'list'
  value_secondary?: string | null
  group_id: number
  sort_order: number
  is_required: boolean
  negate: boolean
}

export interface ClinicalRuleConditionCreate extends ClinicalRuleConditionBase {}

export interface ClinicalRuleConditionUpdate {
  name?: string | null
  description?: string | null
  field_path?: string
  operator?: ConditionOperator
  value?: string | null
  value_type?: 'string' | 'number' | 'boolean' | 'date' | 'list'
  value_secondary?: string | null
  group_id?: number
  sort_order?: number
  is_required?: boolean
  negate?: boolean
}

export interface ClinicalRuleConditionResponse extends ClinicalRuleConditionBase {
  id: string
  rule_id: string
  created_at: string
  updated_at?: string | null
}

export interface ClinicalRuleActionBase {
  action_type: ActionType
  sort_order: number
  config: Record<string, unknown>
}

export interface ClinicalRuleActionCreate extends ClinicalRuleActionBase {}

export interface ClinicalRuleActionUpdate {
  action_type?: ActionType
  sort_order?: number
  config?: Record<string, unknown>
}

export interface ClinicalRuleActionResponse extends ClinicalRuleActionBase {
  id: string
  rule_id: string
  created_at: string
  updated_at?: string | null
}

export interface ClinicalRuleBase {
  name: string
  description?: string | null
  rule_type: RuleType
  severity: RuleSeverity
  triggers: RuleTrigger[]
  applies_to_all_patients: boolean
  applies_to_departments: string[]
  applies_to_patient_tags: string[]
  scheduled_expression?: string | null
  config: Record<string, unknown>
}

export interface ClinicalRuleCreate extends ClinicalRuleBase {
  conditions: ClinicalRuleConditionCreate[]
  actions: ClinicalRuleActionCreate[]
}

export interface ClinicalRuleUpdate {
  name?: string
  description?: string | null
  rule_type?: RuleType
  severity?: RuleSeverity
  status?: RuleStatus
  triggers?: RuleTrigger[]
  applies_to_all_patients?: boolean
  applies_to_departments?: string[]
  applies_to_patient_tags?: string[]
  scheduled_expression?: string | null
  config?: Record<string, unknown>
}

export interface ClinicalRuleResponse extends ClinicalRuleBase {
  id: string
  status: RuleStatus
  version: number
  authored_by?: string | null
  approved_by?: string | null
  approved_at?: string | null
  evaluation_count: number
  last_evaluated_at?: string | null
  match_count: number
  created_at: string
  updated_at?: string | null
}

export interface ClinicalRuleDetailResponse extends ClinicalRuleResponse {
  conditions: ClinicalRuleConditionResponse[]
  actions: ClinicalRuleActionResponse[]
}

export interface ClinicalRuleListResponse {
  items: ClinicalRuleDetailResponse[]
  total: number
  skip: number
  limit: number
}

export interface ClinicalRuleAlertActionResponse {
  id: string
  alert_id: string
  actor_id?: string | null
  action: string
  from_status?: string | null
  to_status?: string | null
  rationale?: string | null
  action_metadata: Record<string, unknown>
  created_at: string
}

export interface ClinicalRuleAlertBase {
  title: string
  message: string
  description?: string | null
  recommended_actions: string[]
  owner_role: string
  due_at?: string | null
}

export interface ClinicalRuleAlertResponse extends ClinicalRuleAlertBase {
  id: string
  alert_key: string
  rule_id: string
  rule_version: number
  evaluation_id: string
  alert_type: RuleType
  severity: RuleSeverity
  status: AlertStatus
  patient_id: string
  prescription_id?: string | null
  appointment_id?: string | null
  lab_result_id?: string | null
  assigned_to?: string | null
  acknowledged_at?: string | null
  acknowledged_by?: string | null
  resolved_at?: string | null
  resolved_by?: string | null
  dismissed_at?: string | null
  dismissed_by?: string | null
  dismissal_reason?: string | null
  last_triggered_at: string
  trigger_count: number
  context: Record<string, unknown>
  can_be_dismissed: boolean
  dismissal_requires_justification: boolean
  safety_event_id?: string | null
  created_at: string
  updated_at?: string | null
}

export interface ClinicalRuleAlertDetailResponse extends ClinicalRuleAlertResponse {
  actions: ClinicalRuleAlertActionResponse[]
}

export interface ClinicalRuleAlertListResponse {
  items: ClinicalRuleAlertResponse[]
  total: number
  skip: number
  limit: number
}

export interface ClinicalRuleAlertStatusUpdate {
  status: AlertStatus
  rationale?: string | null
  assigned_to?: string | null
}

export interface RuleTestRequest {
  patient_id: string
  trigger_type: RuleTrigger
  trigger_entity_type?: string | null
  trigger_entity_id?: string | null
  context_data?: Record<string, unknown>
}

export interface RuleTestConditionResult {
  condition_id?: string | null
  condition_name?: string | null
  field_path: string
  operator: string
  expected_value?: string | null
  actual_value: unknown
  passed: boolean
  error?: string | null
}

export interface RuleTestResponse {
  rule_id: string
  rule_name: string
  tested_at: string
  matched: boolean
  condition_results: RuleTestConditionResult[]
  would_execute_actions: ActionType[]
  evaluation_duration_ms: number
  error?: string | null
}

export interface RuleStatusUpdate {
  status: RuleStatus
  rationale?: string | null
}

export interface RuleApprovalRequest {
  approved: boolean
  comments?: string | null
}

export interface RuleApprovalResponse {
  rule_id: string
  status: RuleStatus
  approved_by?: string | null
  approved_at?: string | null
  comments?: string | null
}

export interface RuleStatistics {
  rule_id: string
  rule_name: string
  evaluation_count: number
  match_count: number
  match_rate_percent: number
  last_evaluated_at?: string | null
  alert_count: number
  acknowledged_count: number
  dismissed_count: number
  resolved_count: number
}

export interface RulesDashboardSummary {
  total_rules: number
  active_rules: number
  draft_rules: number
  paused_rules: number
  total_evaluations_24h: number
  total_alerts_active: number
  alerts_by_severity: Record<string, number>
  top_triggered_rules: RuleStatistics[]
}

export interface FieldPathOption {
  path: string
  label: string
  type: string
  description?: string | null
  example?: string | null
}

export interface FieldPathCategory {
  category: string
  label: string
  fields: FieldPathOption[]
}

export interface FieldPathCatalogResponse {
  categories: FieldPathCategory[]
}

export const clinicalRulesApi = {
  // Get field paths catalog
  getFieldPaths: () =>
    api.get<FieldPathCatalogResponse>('/clinical-rules/field-paths'),

  // Create clinical rule
  create: (data: ClinicalRuleCreate) =>
    api.post<ClinicalRuleDetailResponse>('/clinical-rules', data),

  // List clinical rules
  list: (params?: { status?: RuleStatus; rule_type?: RuleType; trigger?: RuleTrigger; skip?: number; limit?: number }) =>
    api.get<ClinicalRuleListResponse>('/clinical-rules', { params }),

  // Get clinical rule by ID
  get: (ruleId: string) =>
    api.get<ClinicalRuleDetailResponse>(`/clinical-rules/${ruleId}`),

  // Update clinical rule
  update: (ruleId: string, data: ClinicalRuleUpdate) =>
    api.patch<ClinicalRuleResponse>(`/clinical-rules/${ruleId}`, data),

  // Delete clinical rule
  delete: (ruleId: string) =>
    api.delete<void>(`/clinical-rules/${ruleId}`),

  // Approve/reject rule
  approve: (ruleId: string, data: RuleApprovalRequest) =>
    api.post<RuleApprovalResponse>(`/clinical-rules/${ruleId}/approve`, data),

  // Update rule status
  updateStatus: (ruleId: string, data: RuleStatusUpdate) =>
    api.patch<ClinicalRuleResponse>(`/clinical-rules/${ruleId}/status`, data),

  // Test rule
  test: (ruleId: string, data: RuleTestRequest) =>
    api.post<RuleTestResponse>(`/clinical-rules/${ruleId}/test`, data),

  // List alerts
  listAlerts: (params?: { patient_id?: string; rule_id?: string; status?: AlertStatus; severity?: RuleSeverity; owner_role?: string; assigned_to?: string; skip?: number; limit?: number }) =>
    api.get<ClinicalRuleAlertListResponse>('/clinical-rules/alerts', { params }),

  // Get alert by ID
  getAlert: (alertId: string) =>
    api.get<ClinicalRuleAlertDetailResponse>(`/clinical-rules/alerts/${alertId}`),

  // Update alert status
  updateAlertStatus: (alertId: string, data: ClinicalRuleAlertStatusUpdate) =>
    api.patch<ClinicalRuleAlertDetailResponse>(`/clinical-rules/alerts/${alertId}/status`, data),

  // Get dashboard summary
  getDashboardSummary: () =>
    api.get<RulesDashboardSummary>('/clinical-rules/dashboard/summary'),
}
